/* OrganizationRuntimeContext — Organization Runtime Foundation (Phase 0).
   Single GET /orgs/:orgId/runtime call replaces N+1 per-page fetches.
   5-minute memory-only cache per orgId. No localStorage. No sessionStorage. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getRuntime } from '../api/org-runtime.api.js';

/* Module-level cache — survives provider remounts within the same browser session */
const RUNTIME_CACHE = new Map();
const TTL_MS = 5 * 60 * 1000; /* 5 minutes */

function isFresh(entry) {
  return entry && Date.now() - entry.fetchedAt < TTL_MS;
}

export function invalidateRuntime(orgId) {
  if (orgId) {
    RUNTIME_CACHE.delete(orgId);
  } else {
    RUNTIME_CACHE.clear();
  }
}

const OrgRuntimeCtx = createContext(null);

export function OrganizationRuntimeProvider({ orgId, children }) {
  const [runtime, setRuntime]   = useState(() => RUNTIME_CACHE.get(orgId)?.data ?? null);
  const [loading, setLoading]   = useState(!isFresh(RUNTIME_CACHE.get(orgId)));
  const [error, setError]       = useState(null);
  const inflightRef             = useRef(false);
  const fetchingOrgRef          = useRef(null);
  const mountedRef              = useRef(true);

  /* Reset state and unlock inflight when navigating between orgs.
     Declared before fetchRuntime effect so it fires first on orgId change. */
  useEffect(() => {
    inflightRef.current = false;
    const cached = RUNTIME_CACHE.get(orgId);
    if (isFresh(cached)) {
      setRuntime(cached.data);
      setLoading(false);
      setError(null);
    } else {
      setRuntime(null);
      setLoading(true);
      setError(null);
    }
  }, [orgId]);

  const fetchRuntime = useCallback(async (bust = false) => {
    if (!orgId) return;
    if (!bust && inflightRef.current) return;
    const cached = RUNTIME_CACHE.get(orgId);
    if (!bust && isFresh(cached)) {
      setRuntime(cached.data);
      setLoading(false);
      return;
    }
    inflightRef.current = true;
    fetchingOrgRef.current = orgId;
    setLoading(true);
    setError(null);
    try {
      const data = await getRuntime(orgId);
      RUNTIME_CACHE.set(orgId, { data, fetchedAt: Date.now() });
      if (mountedRef.current && fetchingOrgRef.current === orgId) {
        setRuntime(data);
        setLoading(false);
      }
    } catch (err) {
      if (mountedRef.current && fetchingOrgRef.current === orgId) {
        setError(err?.message ?? 'Failed to load organization runtime');
        setLoading(false);
      }
    } finally {
      if (fetchingOrgRef.current === orgId) {
        inflightRef.current = false;
      }
    }
  }, [orgId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchRuntime(false);
    return () => { mountedRef.current = false; };
  }, [fetchRuntime]);

  const refreshRuntime = useCallback(() => {
    RUNTIME_CACHE.delete(orgId);
    fetchRuntime(true);
  }, [orgId, fetchRuntime]);

  /* Optimistically patch branding in local state and module-level cache.
     Writing to the cache prevents navigation within the session from reloading
     stale pre-patch data — the patched values persist until TTL expiry. */
  const patchBranding = useCallback((patch) => {
    setRuntime(prev => {
      if (!prev) return prev;
      const updated = { ...prev, branding: { ...prev.branding, ...patch } };
      const cached = RUNTIME_CACHE.get(orgId);
      if (cached) RUNTIME_CACHE.set(orgId, { data: updated, fetchedAt: cached.fetchedAt });
      return updated;
    });
  }, [orgId]);

  const ctx = useMemo(() => ({
    runtime,
    loading,
    error,
    refreshRuntime,
    patchBranding,
    invalidateRuntime: (id) => invalidateRuntime(id ?? orgId),
  }), [runtime, loading, error, refreshRuntime, patchBranding, orgId]);

  return <OrgRuntimeCtx.Provider value={ctx}>{children}</OrgRuntimeCtx.Provider>;
}

/* ── Hooks ─────────────────────────────────────────────────────────────── */

export function useOrgRuntime() {
  const ctx = useContext(OrgRuntimeCtx);
  if (!ctx) throw new Error('useOrgRuntime must be used inside OrganizationRuntimeProvider');
  return ctx;
}

export function useOrgFeatures() {
  const { runtime } = useOrgRuntime();
  return runtime?.resolvedFeatures ?? [];
}

export function useOrgPermissions() {
  const { runtime } = useOrgRuntime();
  return runtime?.permissions ?? {};
}

export function useOrgNavigation() {
  const { runtime } = useOrgRuntime();
  return runtime?.navigation ?? [];
}

export function useOrganization() {
  const { runtime } = useOrgRuntime();
  return runtime?.org ?? null;
}

export function useSubscription() {
  const { runtime } = useOrgRuntime();
  return runtime?.subscription ?? null;
}

export function useUsage() {
  const { runtime } = useOrgRuntime();
  return runtime?.usage ?? {};
}

export function useLimits() {
  const { runtime } = useOrgRuntime();
  return runtime?.limits ?? {};
}

export function useOrgNotifications() {
  const { runtime } = useOrgRuntime();
  return runtime?.notifications ?? { unreadCount: 0, alerts: [] };
}

export function useCurrentOrgUser() {
  const { runtime } = useOrgRuntime();
  return runtime?.currentUser ?? null;
}

export function useOrgRoles() {
  const { runtime } = useOrgRuntime();
  return runtime?.roles ?? [];
}
